import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { getUrl } from '@ngxp/rest';
import { LoadingGuard, StateResource } from '@schir-int-client/ngrx-helpers';
import {
	createKorrespondenzVorlageAction,
	downloadKorrespondenzVorlageAction,
} from 'libs/korrespondenz-shared/src/lib/korrespondenz-vorlage/korrespondenz-vorlage.actions';
import { KorrespondenzVorlage } from 'libs/korrespondenz-shared/src/lib/korrespondenz-vorlage/korrespondenz-vorlage.model';
import { isNil } from 'lodash-es';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';
import {
	createAufgabeAction,
	deleteAufgabeAction,
	loadAdressatenByAufgabeAction,
	loadAufgabenByVerfuegungAction,
	loadAufgabeSuccessAction,
	markAufgabeAsErledigtAction,
	markAufgabeAsFreigegebenAction,
	updateAufgabeAction,
} from './aufgabe.actions';
import { AdressatWithResource, Aufgabe, AufgabeResource } from './aufgabe.model';
import {
	adressatenByAufgabeSelector,
	aufgabenSelector,
	aufgabeSelector,
	isAufgabeSelectedSelector,
} from './aufgabe.selectors';
import { AufgabeRootState } from './aufgabe.state';
import { verfuegungSelector } from '../../../verfuegung-shared/src/lib/verfuegung.selectors';

@Injectable({ providedIn: 'root' })
export class AufgabeFacade {
	private loadingAufgabenByVerfuegung = new LoadingGuard(this.store);

	aufgabe: Observable<AufgabeResource> = this.store.select(aufgabeSelector);

	constructor(private store: Store<AufgabeRootState>) {}

	createAufgabe(aufgabe: Aufgabe): void {
		this.store.dispatch(createAufgabeAction({ aufgabe }));
	}

	updateAufgabe(aufgabe: Aufgabe): void {
		this.store.dispatch(updateAufgabeAction({ aufgabe }));
	}

	getAdressatenByAufgabe(aufgabe: AufgabeResource): Observable<AdressatWithResource[]> {
		return this.store.select(adressatenByAufgabeSelector(getUrl(aufgabe))).pipe(
			map(adressaten => {
				if (isNil(adressaten)) {
					this.store.dispatch(loadAdressatenByAufgabeAction({ aufgabe }));
					return [];
				}
				return adressaten;
			}),
			share()
		);
	}

	getAufgaben(): Observable<StateResource<AufgabeResource[]>> {
		return combineLatest([this.store.select(verfuegungSelector), this.store.select(aufgabenSelector)]).pipe(
			filter(([verfuegung]) => !isNil(verfuegung) && !isNil(verfuegung.resource)),
			filter(
				([verfuegung, aufgaben]) =>
					!this.loadingAufgabenByVerfuegung.mustLoadFirst(
						aufgaben,
						() => loadAufgabenByVerfuegungAction(),
						verfuegung.resource._links?.self?.href
					)
			),
			map(([, aufgaben]) => aufgaben)
		);
	}

	markAsErledigt(aufgabe: AufgabeResource) {
		this.store.dispatch(markAufgabeAsErledigtAction({ aufgabe }));
	}

	markAsFreigegeben(aufgabe: AufgabeResource) {
		this.store.dispatch(markAufgabeAsFreigegebenAction({ aufgabe }));
	}

	createKorrespondenz(korrespondenzVorlage: KorrespondenzVorlage, vorlageId: number) {
		this.store.dispatch(createKorrespondenzVorlageAction({ korrespondenzVorlage, vorlageId }));
	}

	downloadKorrespondenz() {
		this.store.dispatch(downloadKorrespondenzVorlageAction());
	}

	setAufgabe(aufgabe: AufgabeResource): void {
		this.store.dispatch(loadAufgabeSuccessAction({ aufgabe }));
	}

	isAufgabeSelected(aufgabe: AufgabeResource): Observable<boolean> {
		return this.store.select(isAufgabeSelectedSelector(aufgabe));
	}

	deleteAufgabe(aufgabe: AufgabeResource) {
		this.store.dispatch(deleteAufgabeAction({ aufgabe }));
	}
}
